import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../../context/AuthContext";
import {
  HiMagnifyingGlass,
  HiXMark,
  HiCheckCircle,
  HiPrinter,
  HiChevronLeft,
  HiChevronRight,
  HiFunnel,
  HiPencil,
  HiTrash,
  HiPlus,
  HiChevronDown,
} from "react-icons/hi2";

const CATEGORIAS = ["Transporte", "Hotel", "Gasolina", "Taxi / Uber", "Comida", "Casetas", "Efectivo", "Otros"];
const METODOS_PAGO = ["FinTech", "Efectivo", "Tag", "Uber Bussines", "One Card"];

/* ------------------------------------------------------------------ */
/*  SearchableSelect                                                   */
/* ------------------------------------------------------------------ */
const SearchableSelect = ({ options, value, onChange, placeholder = "Seleccionar...", emptyLabel = null }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = options.find((o) => o.value === value);
  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val) => { onChange(val); setSearch(""); setOpen(false); };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => { setOpen((prev) => !prev); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="input w-full flex items-center justify-between text-left cursor-pointer"
      >
        <span className={selectedOption ? "text-gray-900 truncate" : "text-gray-400 truncate"}>
          {selectedOption ? selectedOption.label : (emptyLabel || placeholder)}
        </span>
        <span className="flex items-center gap-1 text-gray-400 shrink-0 ml-2">
          {value && (
            <HiXMark
              className="h-3.5 w-3.5 hover:text-red-500 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); handleSelect(""); }}
            />
          )}
          <HiChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-[60] mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
            <HiMagnifyingGlass className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
            />
            {search && (
              <HiXMark className="h-4 w-4 shrink-0 cursor-pointer text-gray-400 hover:text-red-500" onClick={() => setSearch("")} />
            )}
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {emptyLabel && (
              <li
                onClick={() => handleSelect("")}
                className={`flex cursor-pointer items-center px-3 py-2 text-sm transition hover:bg-gray-50 ${
                  !value ? "font-medium text-slate-900 bg-gray-50" : "text-gray-500"
                }`}
              >
                {emptyLabel}
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-center text-xs text-gray-400">Sin resultados</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`flex cursor-pointer items-center px-3 py-2 text-sm transition hover:bg-gray-50 ${
                    value === opt.value ? "font-medium text-slate-900 bg-gray-100" : "text-gray-700"
                  }`}
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Pagination                                                         */
/* ------------------------------------------------------------------ */
const Pagination = ({ count, page, rowsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  return (
    <div className="flex items-center justify-end border-t border-gray-200 px-4 py-3 text-sm text-gray-600 gap-3">
      <span>{page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, count)} de {count}</span>
      <button className="btn-icon" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
        <HiChevronLeft className="h-5 w-5" />
      </button>
      <button className="btn-icon" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
        <HiChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

/* ================================================================== */
function GestorGastos() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [allGastos, setAllGastos] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [aprobados, setAprobados] = useState([]);

  const [filterTechnician, setFilterTechnician] = useState("");
  const [filterFechaInicio, setFilterFechaInicio] = useState("");
  const [filterFechaFin, setFilterFechaFin] = useState("");
  const [filterTicket, setFilterTicket] = useState("");
  const [tecnicos, setTecnicos] = useState([]);

  const [pendientesPage, setPendientesPage] = useState(0);
  const [aprobadosPage, setAprobadosPage] = useState(0);
  const [busquedaPage, setBusquedaPage] = useState(0);
  const rowsPerPage = 10;

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [approvingTicket, setApprovingTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState("");

  const [ticketHistoryOpen, setTicketHistoryOpen] = useState(false);
  const [ticketHistoryData, setTicketHistoryData] = useState([]);
  const [selectedTicketNumber, setSelectedTicketNumber] = useState("");
  const [reservaData, setReservaData] = useState(null);

  // Edit gasto
  const [editGastoOpen, setEditGastoOpen] = useState(false);
  const [editGastoData, setEditGastoData] = useState(null);

  // Delete gasto
  const [deleteGastoOpen, setDeleteGastoOpen] = useState(false);
  const [gastoToDelete, setGastoToDelete] = useState(null);

  // New gasto
  const [newGastoOpen, setNewGastoOpen] = useState(false);
  const [savingGasto, setSavingGasto] = useState(false);
  const emptyNewGasto = { fecha_gasto: "", monto: "", metodo_pago: "", categoria: "", nombre_tecnico: "", facturado: false, concepto: "" };
  const [newGastoData, setNewGastoData] = useState(emptyNewGasto);

  /* ----------------------- Derived: client-side filtered search ---------------------- */
  const busqueda = allGastos.filter((g) => {
    if (filterTechnician && g.nombre_tecnico !== filterTechnician) return false;
    if (filterTicket && g.ticket !== filterTicket) return false;
    if (filterFechaInicio) {
      const d = g.fecha_gasto ? g.fecha_gasto.split("T")[0] : "";
      if (d < filterFechaInicio) return false;
    }
    if (filterFechaFin) {
      const d = g.fecha_gasto ? g.fecha_gasto.split("T")[0] : "";
      if (d > filterFechaFin) return false;
    }
    return true;
  });

    /* ----------------------- Formatters ---------------------- */
  const formatUTCDate = (dateString) => {
    if (!dateString) return "";
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  };

  const formatAmount = (amount) =>
    `$ ${Number(amount).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;

  /* ----------------------- Fetchers ---------------------- */
  const fetchAllGastos = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/gastos_tecnicos/busqueda");
      setAllGastos(response.data);
    } catch (error) {
      console.error("Error fetching all gastos:", error);
    }
  };

  const fetchPendientes = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/gastos_tecnicos/no_aprobados");
      setPendientes(response.data);
    } catch (error) {
      console.error("Error al obtener gastos pendientes:", error);
    }
  };

  const fetchAprobados = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/gastos_tecnicos/aprobados");
      setAprobados(response.data);
    } catch (error) {
      console.error("Error al obtener gastos aprobados:", error);
    }
  };

  const fetchTecnicos = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/tecnicos_activos");
      setTecnicos(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchReservaDataById = async (idReserva) => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${idReserva}`);
      setReservaData(response.data);
    } catch (error) {
      console.error("Error fetching reserva data:", error);
    }
  };

  const handleClearFilters = () => {
    setFilterTechnician("");
    setFilterFechaInicio("");
    setFilterFechaFin("");
    setFilterTicket("");
    setPendientesPage(0);
    setAprobadosPage(0);
  };

  useEffect(() => {
    setBusquedaPage(0);
  }, [filterTechnician, filterFechaInicio, filterFechaFin, filterTicket]);

  // Auto-clear ticket when technician changes and the current ticket no longer belongs to them
  useEffect(() => {
    if (filterTicket) {
      const validTickets = allGastos
        .filter((g) => !filterTechnician || g.nombre_tecnico === filterTechnician)
        .map((g) => g.ticket);
      if (!validTickets.includes(filterTicket)) {
        setFilterTicket("");
      }
    }
  }, [filterTechnician]);

  useEffect(() => {
    fetchTecnicos();
    fetchAllGastos();
    fetchPendientes();
    fetchAprobados();
  }, []);

  /* ----------------------- Approve ---------------------- */
  const handleOpenApprovalByTicket = (ticketNumber) => {
    setSelectedTicket(ticketNumber);
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setSelectedTicket("");
  };

  const confirmApproveTicket = async () => {
    if (selectedTicket) {
      setApprovingTicket(true);
      try {
        const expensesForTicket = pendientes.filter((item) => item.ticket === selectedTicket);
        for (const expense of expensesForTicket) {
          await axios.put(`https://desarrollotecnologicoar.com/api5/gastos_tecnicos/aprobar/${expense.id}`);
        }

        // Obtener datos del técnico para el correo de notificación
        const primerGasto = expensesForTicket[0] || {};
        let correoTecnico = primerGasto.correo_tecnico;
        if (!correoTecnico && primerGasto.nombre_tecnico) {
          const tecEncontrado = tecnicos.find((t) => t.nombre_tecnico === primerGasto.nombre_tecnico);
          if (tecEncontrado?.correo_tecnico) {
            correoTecnico = tecEncontrado.correo_tecnico;
          }
        }

        if (correoTecnico) {
          try {
            const aprobadoPor = user?.displayName || user?.email || "Administrador";
            const d = new Date();
            const fechaHoy = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
            const token = "Desarrollo1!";

            const urlNotificacion = `https://script.google.com/macros/s/AKfycbwi1ggxf2DetWencda3QYV7QF0As5cxGmEO5FTxp3OgRpAwvL9l7rBT4evEu1OcvJwWvQ/exec?token=${encodeURIComponent(token)}&correo=${encodeURIComponent(correoTecnico)}&ticket=${encodeURIComponent(selectedTicket)}&aprobado_por=${encodeURIComponent(aprobadoPor)}&fecha=${encodeURIComponent(fechaHoy)}`;

            await fetch(urlNotificacion, {
              method: "GET",
              mode: "no-cors",
            });
          } catch (emailErr) {
            console.error("Error enviando correo de aprobación:", emailErr);
          }
        }

        fetchPendientes();
        fetchAprobados();
        fetchAllGastos();
      } catch (error) {
        console.error("Error al aprobar el ticket completo:", error);
      } finally {
        setApprovingTicket(false);
      }
    }
    handleCloseConfirmation();
  };

  /* ----------------------- Ticket history ---------------------- */
  const handleTicketClick = (ticket) => {
    const history = busqueda.filter((item) => item.ticket === ticket);
    setSelectedTicketNumber(ticket);
    setTicketHistoryData(history);
    setTicketHistoryOpen(true);
    if (history.length > 0 && history[0].id_reserva) {
      fetchReservaDataById(history[0].id_reserva);
    }
  };

  const handleCloseTicketHistory = () => {
    setTicketHistoryOpen(false);
    setTicketHistoryData([]);
    setSelectedTicketNumber("");
  };

  /* ----------------------- Edit gasto ---------------------- */
  const handleEditGasto = (gasto) => {
    setEditGastoData({ ...gasto, fecha_gasto: gasto.fecha_gasto ? gasto.fecha_gasto.split("T")[0] : "" });
    setEditGastoOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editGastoData) return;
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/actualizar_gastos/${editGastoData.id}`, {
        monto: editGastoData.monto,
        metodo_pago: editGastoData.metodo_pago,
        categoria: editGastoData.categoria,
        facturado: editGastoData.facturado,
        concepto: editGastoData.concepto,
        fecha_gasto: editGastoData.fecha_gasto,
      });
      const updated = ticketHistoryData.map((g) => g.id === editGastoData.id ? { ...g, ...editGastoData } : g);
      setTicketHistoryData(updated);
      setEditGastoOpen(false);
      setEditGastoData(null);
      fetchAllGastos();
      fetchPendientes();
      fetchAprobados();
    } catch (error) {
      console.error("Error al actualizar el gasto:", error);
    }
  };

  /* ----------------------- Delete gasto ---------------------- */
  const handleDeleteGasto = (gasto) => {
    setGastoToDelete(gasto);
    setDeleteGastoOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!gastoToDelete) return;
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api5/eliminar_gasto/${gastoToDelete.id}`);
      setTicketHistoryData((prev) => prev.filter((g) => g.id !== gastoToDelete.id));
      setDeleteGastoOpen(false);
      setGastoToDelete(null);
      fetchAllGastos();
      fetchPendientes();
      fetchAprobados();
    } catch (error) {
      console.error("Error al eliminar el gasto:", error);
    }
  };

  /* ----------------------- New gasto ---------------------- */
  const handleOpenNewGasto = () => {
    const ref = ticketHistoryData[0] || {};
    setNewGastoData({
      ...emptyNewGasto,
      nombre_tecnico: ref.nombre_tecnico || "",
      correo_tecnico: ref.correo_tecnico || "",
    });
    setNewGastoOpen(true);
  };

  const handleSaveNewGasto = async () => {
    const ref = ticketHistoryData[0] || {};
    setSavingGasto(true);
    try {
      await axios.post("https://desarrollotecnologicoar.com/api5/registrar_gastos", {
        id_reserva: ref.id_reserva || "",
        ticket: selectedTicketNumber,
        monto: newGastoData.monto,
        metodo_pago: newGastoData.metodo_pago,
        categoria: newGastoData.categoria,
        nombre_tecnico: newGastoData.nombre_tecnico,
        correo_tecnico: newGastoData.correo_tecnico || ref.correo_tecnico || "",
        fecha: ref.fecha || new Date().toISOString(),
        facturado: newGastoData.facturado,
        concepto: newGastoData.concepto,
        fecha_gasto: newGastoData.fecha_gasto,
      });
      setNewGastoOpen(false);
      setNewGastoData(emptyNewGasto);
      await fetchAllGastos();
      // Reload ticket history from refreshed allGastos is handled via re-render,
      // but we reload it explicitly to update the open dialog immediately
      const historyRes = await axios.get("https://desarrollotecnologicoar.com/api5/gastos_tecnicos/busqueda");
      const fresh = historyRes.data.filter((item) => item.ticket === selectedTicketNumber);
      setTicketHistoryData(fresh);
      fetchPendientes();
      fetchAprobados();
    } catch (error) {
      console.error("Error al crear el gasto:", error);
    } finally {
      setSavingGasto(false);
    }
  };

  /* ----------------------- PDF generation ---------------------- */
  const handlePrintPDF = () => {
    if (!ticketHistoryData || ticketHistoryData.length === 0) return;
    const doc = new jsPDF();
    // Referencias digitales provisionales de la guía de identidad visual.
    const colors = { red: [255, 0, 0], dark: [22, 23, 24], gray: [204, 204, 204] };
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    const ticket = reservaData?.ticket || selectedTicketNumber;
    const logo = `${process.env.PUBLIC_URL || ""}/logo_asiarob.jpg`;
    const logoInfo = doc.getImageProperties(logo);
    const logoWidth = 48;
    const logoHeight = logoWidth * logoInfo.height / logoInfo.width;
    const generatedAt = new Date().toLocaleString("es-MX");
    doc.setProperties({ title: `Reporte de gastos - Ticket #${ticket}`, author: "AR Tecnología SA de CV" });

    const drawHeader = () => {
      doc.addImage(logo, "JPEG", margin, 12, logoWidth, logoHeight);
      doc.setTextColor(...colors.dark);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("AR Tecnología SA de CV", pageWidth - margin, 16, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Av. 8 de Julio 1626, Morelos,", pageWidth - margin, 22, { align: "right" });
      doc.text("44919 Guadalajara, Jal.", pageWidth - margin, 27, { align: "right" });
      doc.setDrawColor(...colors.red);
      doc.setLineWidth(0.8);
      doc.line(margin, 33, pageWidth - margin, 33);
    };

    let startY = 44;
    const writeDetail = (label, value) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(`${label}: ${value || "No disponible"}`, contentWidth);
      lines.forEach((line) => {
        if (startY > pageHeight - 25) {
          doc.addPage();
          startY = 43;
        }
        doc.text(line, margin, startY);
        startY += 5;
      });
      startY += 2;
    };
    doc.setTextColor(...colors.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Reporte de gastos", margin, startY);
    startY += 8;
    writeDetail("Ticket", ticket);
    if (reservaData) {
      if (reservaData.itinerario?.length > 0) {
        const sortedItinerario = [...reservaData.itinerario].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        const dateOptions = { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" };
        writeDetail("Fecha inicio", new Date(sortedItinerario[0].fecha).toLocaleDateString("es-MX", dateOptions));
        writeDetail("Fecha final", new Date(sortedItinerario[sortedItinerario.length - 1].fecha).toLocaleDateString("es-MX", dateOptions));
      }
      writeDetail("Cliente", reservaData.empresa);
      writeDetail("Equipo", reservaData.equipo);
      writeDetail("Contacto", reservaData.contacto);
      writeDetail("Tipo de servicio", reservaData.tipo_servicio);
    } else {
      writeDetail("Reserva", "Datos no disponibles");
    }
    startY += 4;
    const drawSection = (title) => {
      // Reservar espacio para el título, el encabezado y la primera fila.
      if (startY > pageHeight - 65) {
        doc.addPage();
        startY = 43;
      }
      doc.setFillColor(...colors.red);
      doc.rect(margin, startY - 4, 1.2, 5, "F");
      doc.setTextColor(...colors.dark);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(title, margin + 4, startY);
      startY += 4;
    };
    const tableStyles = {
      theme: "plain",
      margin: { top: 40, bottom: 23, left: margin, right: margin },
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, overflow: "linebreak", textColor: colors.dark, valign: "middle" },
      headStyles: { fillColor: colors.dark, textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      footStyles: { fillColor: [235, 235, 235], textColor: colors.dark, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 21 }, 1: { cellWidth: 27 }, 2: { cellWidth: 23 }, 3: { cellWidth: 20 }, 4: { cellWidth: 24 }, 5: { cellWidth: 27, halign: "right" } },
      showFoot: "lastPage",
      rowPageBreak: "avoid",
    };
    const tableColumns = ["Fecha", "Técnico", "Concepto", "Facturado", "Método de pago", "Monto", "Descripción"];
    const viaticosData = ticketHistoryData.filter(
      (item) => item.metodo_pago === "One Card" || (item.categoria === "Efectivo" && item.facturado === true) || item.facturado === true
    );
    const viaticosRows = viaticosData.map((item) => [
      formatUTCDate(item.fecha_gasto), item.nombre_tecnico, item.categoria,
      item.facturado === true ? "Sí" : "No", item.metodo_pago || "", formatAmount(item.monto), item.concepto,
    ]);
    const totalViaticos = viaticosData.reduce((acc, item) => acc + Number(item.monto), 0);
    drawSection("Viáticos");
    autoTable(doc, {
      head: [tableColumns],
      body: viaticosRows,
      foot: [[{ content: "Total Viáticos", colSpan: 5, styles: { halign: "right", fontStyle: "bold" } }, { content: formatAmount(totalViaticos), styles: { halign: "right" } }, ""]],
      ...tableStyles,
      startY,
    });
    startY = doc.lastAutoTable.finalY + 10;
    drawSection("Gastos generales");
    const generalRows = ticketHistoryData.map((item) => [
      formatUTCDate(item.fecha_gasto), item.nombre_tecnico, item.categoria,
      item.facturado === true ? "Sí" : "No", item.metodo_pago || "", formatAmount(item.monto), item.concepto,
    ]);
    const totalGeneral = ticketHistoryData.reduce((acc, item) => acc + Number(item.monto), 0);
    autoTable(doc, {
      head: [tableColumns],
      body: generalRows,
      foot: [[{ content: "Total Gastos Generales", colSpan: 5, styles: { halign: "right", fontStyle: "bold" } }, { content: formatAmount(totalGeneral), styles: { halign: "right" } }, ""]],
      ...tableStyles,
      startY,
    });
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      drawHeader();
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.2);
      doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text(`Ticket #${ticket} | Generado: ${generatedAt}`, margin, pageHeight - 12);
      doc.text(`Página ${page} de ${pageCount}`, pageWidth - margin, pageHeight - 12, { align: "right" });
    }
    doc.save(`ticket_${selectedTicketNumber}_reporte.pdf`);
  };

  /* ----------------------- Group by ticket ---------------------- */
  const groupByTicket = (data) => {
    const grouped = data.reduce((acc, gasto) => {
      if (!acc[gasto.ticket]) {
        acc[gasto.ticket] = { items: [], allApproved: true };
      }
      acc[gasto.ticket].items.push(gasto);
      if (gasto.aprobado === false) acc[gasto.ticket].allApproved = false;
      return acc;
    }, {});
    return Object.keys(grouped).map((ticket) => {
      const { items, allApproved } = grouped[ticket];
      const totalMonto = items.reduce((sum, e) => sum + Number(e.monto), 0);
      const f = items[0];
      return { ticket, gastos: items, monto: totalMonto, categoria: f.categoria, nombre_tecnico: f.nombre_tecnico, fecha: f.fecha_gasto, concepto: f.concepto, allApproved };
    });
  };

  const hayFiltros = filterTechnician || filterFechaInicio || filterFechaFin || filterTicket;
  const totalPendientes = groupByTicket(pendientes.filter((i) => i.aprobado === false)).length;
  const totalAprobados = groupByTicket(aprobados.filter((i) => i.aprobado === true)).length;

  /* ----------------------- Render table helper ---------------------- */
  const renderGroupedTable = (groupedData, showApproveBtn) => (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="table-header">Monto Total</th>
            <th className="table-header">Categoría</th>
            <th className="table-header">Técnico</th>
            <th className="table-header">Fecha</th>
            <th className="table-header">Ticket</th>
            {showApproveBtn && <th className="table-header">Acción</th>}
          </tr>
        </thead>
        <tbody>
          {groupedData.map((item, index) => (
            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td className="table-cell font-semibold tabular-nums">{formatAmount(item.monto)}</td>
              <td className="table-cell">{item.categoria}</td>
              <td className="table-cell">{item.nombre_tecnico}</td>
              <td className="table-cell whitespace-nowrap">{formatUTCDate(item.fecha)}</td>
              <td className="table-cell">
                <button className="text-blue-600 hover:underline font-medium" onClick={() => handleTicketClick(item.ticket)}>
                  {item.ticket}
                </button>
              </td>
              {showApproveBtn && (
                <td className="table-cell">
                  {(!item.allApproved || showApproveBtn === "force") && (
                    <button className="btn-primary text-xs py-1.5 px-3" onClick={() => handleOpenApprovalByTicket(item.ticket)}>
                      Aprobar
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* ================================================================== */
  /*  tabs                                                              */
  /* ================================================================== */
  const tabs = hayFiltros
    ? [{ label: "Búsqueda", value: 2 }]
    : [
        { label: "Pendientes", value: 0 },
        { label: "Aprobados", value: 1 },
      ];

  const currentTab = hayFiltros ? 2 : activeTab;

  return (
    <div className="page-container max-w-6xl">
      <div className="card">
        {/* Title */}
        <h1 className="page-title text-center mb-6">Gestor de Gastos Técnicos</h1>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
          <div className="flex items-center gap-2 text-gray-500 text-sm font-medium">
            <HiFunnel className="h-4 w-4" />
            Filtros
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className="label">Técnico</label>
            <SearchableSelect
              options={tecnicos.map((tec) => ({ value: tec.nombre_tecnico, label: tec.nombre_tecnico }))}
              value={filterTechnician}
              onChange={setFilterTechnician}
              emptyLabel="Todos"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="label">Fecha inicio</label>
            <div className="relative">
              <input type="date" className="input w-full pr-8" value={filterFechaInicio} onChange={(e) => setFilterFechaInicio(e.target.value)} />
              {filterFechaInicio && (
                <button className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setFilterFechaInicio("")}>
                  <HiXMark className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          <div className="min-w-[160px]">
            <label className="label">Fecha fin</label>
            <div className="relative">
              <input type="date" className="input w-full pr-8" value={filterFechaFin} onChange={(e) => setFilterFechaFin(e.target.value)} />
              {filterFechaFin && (
                <button className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setFilterFechaFin("")}>
                  <HiXMark className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className="label">Ticket</label>
            <SearchableSelect
              options={[
                ...new Set(
                  allGastos
                    .filter((g) => !filterTechnician || g.nombre_tecnico === filterTechnician)
                    .map((g) => g.ticket)
                    .filter(Boolean)
                ),
              ].map((t) => ({ value: t, label: t }))}
              value={filterTicket}
              onChange={setFilterTicket}
              emptyLabel="Todos"
            />
          </div>

          <button className="btn-secondary whitespace-nowrap" onClick={handleClearFilters}>
            Limpiar filtros
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map((t) => (
            <button
              key={t.value}
              onClick={() => !hayFiltros && setActiveTab(t.value)}
              className={`px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap border-b-2 ${
                currentTab === t.value
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {hayFiltros ? (() => {
          const busquedaGrouped = groupByTicket(busqueda);
          const totalBusqueda = busquedaGrouped.length;
          return totalBusqueda === 0 ? (
            <p className="empty-state">No hay resultados.</p>
          ) : (
            <>
              {renderGroupedTable(
                busquedaGrouped.slice(
                  busquedaPage * rowsPerPage,
                  busquedaPage * rowsPerPage + rowsPerPage
                ),
                true
              )}
              {totalBusqueda > rowsPerPage && (
                <Pagination
                  count={totalBusqueda}
                  page={busquedaPage}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setBusquedaPage}
                />
              )}
            </>
          );
        })() : currentTab === 0 ? (
          totalPendientes === 0 ? (
            <p className="empty-state">No hay gastos pendientes.</p>
          ) : (
            <>
              {renderGroupedTable(
                groupByTicket(pendientes.filter((i) => i.aprobado === false)).slice(
                  pendientesPage * rowsPerPage,
                  pendientesPage * rowsPerPage + rowsPerPage
                ),
                "force"
              )}
              {totalPendientes > rowsPerPage && (
                <Pagination count={totalPendientes} page={pendientesPage} rowsPerPage={rowsPerPage} onPageChange={setPendientesPage} />
              )}
            </>
          )
        ) : currentTab === 1 ? (
          totalAprobados === 0 ? (
            <p className="empty-state">No hay gastos aprobados.</p>
          ) : (
            <>
              {renderGroupedTable(
                groupByTicket(aprobados.filter((i) => i.aprobado === true)).slice(
                  aprobadosPage * rowsPerPage,
                  aprobadosPage * rowsPerPage + rowsPerPage
                ),
                false
              )}
              {totalAprobados > rowsPerPage && (
                <Pagination count={totalAprobados} page={aprobadosPage} rowsPerPage={rowsPerPage} onPageChange={setAprobadosPage} />
              )}
            </>
          )
        ) : null}
      </div>

      {/* ========================== Confirm Dialog ========================== */}
      {confirmationOpen && (
        <>
          <div className="overlay" onClick={handleCloseConfirmation} />
          <div className="modal max-w-sm w-full">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Confirmar Aprobación</h2>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro que deseas aprobar todos los gastos de este ticket?
            </p>
            <div className="flex justify-end gap-3">
              <button className="btn-secondary" disabled={approvingTicket} onClick={handleCloseConfirmation}>Cancelar</button>
              <button className="btn-primary" disabled={approvingTicket} onClick={confirmApproveTicket}>
                {approvingTicket ? "Aprobando..." : "Sí, aprobar"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ========================== Ticket History Dialog ========================== */}
      {ticketHistoryOpen && (
        <>
          <div className="overlay" onClick={handleCloseTicketHistory} />
          <div className="modal !fixed max-w-4xl w-full !p-4 !rounded-xl max-h-[90vh] overflow-y-auto">
            {savingGasto && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75 rounded-xl gap-3">
                <svg className="animate-spin h-9 w-9 text-slate-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <span className="text-sm font-medium text-slate-700">Guardando gasto...</span>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">
                Histórico para el ticket #{selectedTicketNumber}
              </h2>
              <div className="flex items-center gap-2">
                <button className="btn-primary flex items-center gap-1 text-xs py-1.5 px-3" onClick={handleOpenNewGasto}>
                  <HiPlus className="h-4 w-4" />
                  Nuevo gasto
                </button>
                <button className="btn-icon" onClick={handleCloseTicketHistory}>
                  <HiXMark className="h-5 w-5" />
                </button>
              </div>
            </div>

            {(ticketHistoryData.length > 0 || newGastoOpen) ? (
              <div className="overflow-x-auto [&_.table-cell]:px-3 [&_.table-cell]:py-2 [&_.table-header]:px-3 [&_.table-header]:py-2">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="table-header">Fecha</th>
                      <th className="table-header">Monto</th>
                      <th className="table-header">Concepto</th>
                      <th className="table-header">Facturado</th>
                      <th className="table-header">Forma de Pago</th>
                      <th className="table-header">Categoría</th>
                      <th className="table-header">Técnico</th>
                      <th className="table-header">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketHistoryData.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="table-cell whitespace-nowrap">{formatUTCDate(item.fecha_gasto)}</td>
                        <td className="table-cell font-semibold tabular-nums">{formatAmount(item.monto)}</td>
                        <td className="table-cell">{item.concepto}</td>
                        <td className="table-cell">
                          <span className={`badge ${item.facturado ? "badge-green" : "badge-gray"}`}>
                            {item.facturado === true ? "Sí" : "No"}
                          </span>
                        </td>
                        <td className="table-cell">{item.metodo_pago || ""}</td>
                        <td className="table-cell">{item.categoria}</td>
                        <td className="table-cell">{item.nombre_tecnico}</td>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <button className="btn-icon text-blue-600 hover:text-blue-800" onClick={() => handleEditGasto(item)} title="Editar">
                              <HiPencil className="h-4 w-4" />
                            </button>
                            <button className="btn-icon text-red-500 hover:text-red-700" onClick={() => handleDeleteGasto(item)} title="Eliminar">
                              <HiTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {newGastoOpen && (
                      <tr className="border-b border-blue-100 bg-blue-50">
                        <td className="table-cell">
                          <input type="date" className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" value={newGastoData.fecha_gasto} onChange={(e) => setNewGastoData({ ...newGastoData, fecha_gasto: e.target.value })} />
                        </td>
                        <td className="table-cell">
                          <input type="number" className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" value={newGastoData.monto} placeholder="0.00" onChange={(e) => setNewGastoData({ ...newGastoData, monto: e.target.value })} />
                        </td>
                        <td className="table-cell">
                          <input type="text" className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" value={newGastoData.concepto} placeholder="Concepto" onChange={(e) => setNewGastoData({ ...newGastoData, concepto: e.target.value })} />
                        </td>
                        <td className="table-cell text-center">
                          <input type="checkbox" className="h-4 w-4" checked={!!newGastoData.facturado} onChange={(e) => setNewGastoData({ ...newGastoData, facturado: e.target.checked })} />
                        </td>
                        <td className="table-cell">
                          <select className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" value={newGastoData.metodo_pago} onChange={(e) => setNewGastoData({ ...newGastoData, metodo_pago: e.target.value })}>
                            <option value="">Seleccionar</option>
                            {METODOS_PAGO.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </td>
                        <td className="table-cell">
                          <select className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" value={newGastoData.categoria} onChange={(e) => setNewGastoData({ ...newGastoData, categoria: e.target.value })}>
                            <option value="">Seleccionar</option>
                            {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className="table-cell">
                          <SearchableSelect
                            options={tecnicos.map((t) => ({ value: t.nombre_tecnico, label: t.nombre_tecnico }))}
                            value={newGastoData.nombre_tecnico}
                            onChange={(val) => setNewGastoData({ ...newGastoData, nombre_tecnico: val })}
                            placeholder="Técnico"
                          />
                        </td>
                        <td className="table-cell py-2">
                          <div className="flex items-center gap-2">
                            <button className="btn-icon text-green-600 hover:text-green-800" onClick={handleSaveNewGasto} title="Guardar">
                              <HiCheckCircle className="h-4 w-4" />
                            </button>
                            <button className="btn-icon text-gray-400 hover:text-gray-600" onClick={() => { setNewGastoOpen(false); setNewGastoData(emptyNewGasto); }} title="Cancelar">
                              <HiXMark className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-50 font-bold">
                      <td className="table-cell text-right">Total:</td>
                      <td className="table-cell tabular-nums">
                        {formatAmount(ticketHistoryData.reduce((acc, item) => acc + Number(item.monto), 0))}
                      </td>
                      <td colSpan={6} />
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-state">No hay histórico para este ticket.</p>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button className="btn-secondary flex items-center gap-2" onClick={handlePrintPDF}>
                <HiPrinter className="h-4 w-4" />
                Imprimir PDF
              </button>
              <button className="btn-primary" onClick={handleCloseTicketHistory}>Cerrar</button>
            </div>
          </div>
        </>
      )}

      {/* ========================== Edit Gasto Dialog ========================== */}
      {editGastoOpen && editGastoData && (
        <>
          <div className="overlay" onClick={() => setEditGastoOpen(false)} />
          <div className="modal max-w-lg w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Editar Gasto</h2>
              <button className="btn-icon" onClick={() => setEditGastoOpen(false)}>
                <HiXMark className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Fecha gasto</label>
                <input type="date" className="input w-full" value={editGastoData.fecha_gasto || ""} onChange={(e) => setEditGastoData({ ...editGastoData, fecha_gasto: e.target.value })} />
              </div>
              <div>
                <label className="label">Monto (MXN)</label>
                <input type="number" className="input w-full" value={editGastoData.monto || ""} onChange={(e) => setEditGastoData({ ...editGastoData, monto: e.target.value })} />
              </div>
              <div>
                <label className="label">Categoría</label>
                <select className="input w-full" value={editGastoData.categoria || ""} onChange={(e) => setEditGastoData({ ...editGastoData, categoria: e.target.value })}>
                  <option value="">Seleccionar</option>
                  {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Método de pago</label>
                <select className="input w-full" value={editGastoData.metodo_pago || ""} onChange={(e) => setEditGastoData({ ...editGastoData, metodo_pago: e.target.value })}>
                  <option value="">Seleccionar</option>
                  {METODOS_PAGO.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="label">Concepto</label>
                <input type="text" className="input w-full" value={editGastoData.concepto || ""} onChange={(e) => setEditGastoData({ ...editGastoData, concepto: e.target.value })} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="edit-facturado" className="h-4 w-4" checked={!!editGastoData.facturado} onChange={(e) => setEditGastoData({ ...editGastoData, facturado: e.target.checked })} />
                <label htmlFor="edit-facturado" className="text-sm text-gray-700">Facturado</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button className="btn-secondary" onClick={() => setEditGastoOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSaveEdit}>Guardar cambios</button>
            </div>
          </div>
        </>
      )}

      {/* ========================== Delete Gasto Dialog ========================== */}
      {deleteGastoOpen && gastoToDelete && (
        <>
          <div className="overlay" onClick={() => setDeleteGastoOpen(false)} />
          <div className="modal max-w-sm w-full">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Eliminar Gasto</h2>
            <p className="text-sm text-gray-600 mb-1">
              ¿Estás seguro que deseas eliminar este gasto?
            </p>
            <p className="text-sm font-semibold text-gray-800 mb-6">
              {gastoToDelete.categoria} — {formatAmount(gastoToDelete.monto)} — {gastoToDelete.concepto}
            </p>
            <div className="flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setDeleteGastoOpen(false)}>Cancelar</button>
              <button className="btn-primary bg-red-600 hover:bg-red-700 focus:ring-red-500" onClick={handleConfirmDelete}>Eliminar</button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export default GestorGastos;
